/* ============================================================================
 * cabinet-visualizer.js — UI layer for the RTA cabinet color visualizer.
 *
 * Pairs with the Design handoff engine (cabinet-recolor.js) and palette
 * (sw-colors.js). This file builds the picker UI, mounts the live recolor on
 * the page's cabinet photos, persists the choice, writes it to the cart, and
 * (on product pages) injects the line-item properties into the add-to-cart form.
 *
 * It is intentionally framework-free and defensive: every integration point that
 * depends on the specific Porto theme markup is a CONFIG selector you can tune
 * from the section settings, and anything it can't find it simply skips (the
 * original photo stays visible — nothing breaks).
 * ==========================================================================*/
(function () {
  'use strict';

  if (!window.CabinetRecolor) {
    console.warn('[cabinet-visualizer] cabinet-recolor.js not loaded; aborting.');
    return;
  }

  var CR = window.CabinetRecolor;
  var COLORS = (window.SW_COLORS || []).slice();
  // Standard inset finishes (Snow White / Vintage White / Tuscan Gray): no SW
  // code, offered as pills above the SW picker (sw-colors.js).
  var STANDARD = (window.RTA_STANDARD_FINISHES || []).slice();
  // The "Front color" label: "Naval Blue SW 6244", or the bare name for a
  // standard finish ("Tuscan Gray").
  function colorLabel(sw) { return sw.code ? sw.name + ' ' + sw.code : sw.name; }
  function configHexToRgb(hex) {
    if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return null;
    return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
  }

  // ---- read config injected by the Liquid section --------------------------
  var CFG = window.RTA_VIZ_CONFIG || {};
  var BOX = configHexToRgb(CFG.boxColor) || CR.BOX_BEIGE;
  // Collection-grid image selectors. The configured selector (if any) is tried
  // first, then known Dawn 15 collection-card markup as fallback, so every
  // product card photo (primary + hover) mounts even when the section's
  // optional grid_selector setting is blank. Masks gate everything: images
  // without a manifest entry are never touched.
  var GRID_FALLBACKS = [
    '#product-grid .card__media img',
    '.product-grid .card__media img',
    '#product-grid .card__inner img',
    '.collection .card__media img'
  ];
  var GRID_SELECTOR = [CFG.gridSelector || ''].concat(GRID_FALLBACKS)
    .filter(Boolean).join(', ');                     // collection grid <img> selector
  // Product-page gallery selectors. The configured selector (if any) is tried
  // first, then known Porto 4 / Dawn gallery markup as fallback so every slide
  // and thumbnail mounts even if the saved section setting targets one element.
  var PRODUCT_FALLBACKS = [
    '.product-single__photos img',
    '.product-single__photo img',
    '.product-single__thumbnails img',
    '.product-images img',
    '.product-gallery img',
    '.product__media img'
  ];
  // recolorPhotos:false (set by the color bar for false doors and panels) leaves
  // the product photos as shot: the color is still chosen and sent to the
  // order, but the tint engine is not run over the gallery.
  var PRODUCT_SELECTOR = CFG.recolorPhotos === false ? '' :
    [CFG.productSelector || ''].concat(PRODUCT_FALLBACKS).filter(Boolean).join(', ');
  var DEFAULT_HEX = CFG.defaultHex || null;          // null => leave photos as-is until a pick
  // Premium Box: every cabinet box renders with the Custom Oak grain texture,
  // before and after a color is picked.
  var PREMIUM = !!CFG.boxTexture && typeof CR.loadBoxTexture === 'function';
  var texState = PREMIUM ? 'loading' : 'off';        // loading | ready | failed | off

  // ---- Standard / Premium Box (custom inset) --------------------------------
  // Premium Box is the default. The choice is made on the box cards
  // (snippets/box-comparison.liquid), sticks across pages, repaints every
  // photo and rides to the cart as the "Box" property.
  var BOX_KEY = 'rta_box';
  var BOX_PROP = 'Box';
  function loadBox() {
    try { return localStorage.getItem(BOX_KEY) === 'standard' ? 'standard' : 'premium'; } catch (e) { return 'premium'; }
  }
  var boxChoice = loadBox();
  function boxLabel() { return boxChoice === 'standard' ? 'Standard Box' : 'Premium Box'; }

  // ---- hue bucketing (works at any list size, no manual family tags) --------
  var BUCKETS = ['Whites & Neutrals', 'Reds', 'Oranges & Browns', 'Yellows & Golds', 'Greens', 'Blues', 'Purples & Pinks'];

  function hexToHsl(hex) {
    var r = parseInt(hex.slice(1, 3), 16) / 255,
        g = parseInt(hex.slice(3, 5), 16) / 255,
        b = parseInt(hex.slice(5, 7), 16) / 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
    var l = (mx + mn) / 2, s = 0, h = 0;
    if (d) {
      s = d / (1 - Math.abs(2 * l - 1));
      if (mx === r) h = ((g - b) / d) % 6;
      else if (mx === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60; if (h < 0) h += 360;
    }
    return { h: h, s: s, l: l };
  }

  function bucketOf(hex) {
    var c = hexToHsl(hex);
    if (c.s < 0.14 || c.l > 0.92 || c.l < 0.08) return 'Whites & Neutrals';
    var h = c.h;
    if (h < 15 || h >= 345) return 'Reds';
    if (h < 45) return 'Oranges & Browns';
    if (h < 70) return 'Yellows & Golds';
    if (h < 170) return 'Greens';
    if (h < 260) return 'Blues';
    return 'Purples & Pinks';
  }

  // ---- recolor instances on this page --------------------------------------
  var instances = [];
  window.__rtaInstances = instances; // shared with the product-page bar (snapshot)

  // Paint at the size the photo is shown (x screen density), 760-1500px, so
  // mask edges land on real pixels instead of being stretched.
  function targetSize(imgEl) {
    var w = Math.max(imgEl.clientWidth || 0, imgEl.clientHeight || 0);
    if (!w) return 1200;                            // hidden slide: not measured yet
    return Math.min(1500, Math.max(760, Math.ceil(w * (window.devicePixelRatio || 1))));
  }
  // Ask the Shopify CDN for the photo at that width (drops _1200x-style suffixes).
  function sizedSrc(src, px) {
    if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(src)) return src;
    try {
      var u = new URL(src, location.href);
      u.pathname = u.pathname.replace(/_(\d+x\d*|\d*x\d+)(\.\w+)$/, '$2');
      u.searchParams.set('width', px);
      return u.toString();
    } catch (e) { return src; }
  }

  function mountImage(imgEl) {
    if (!imgEl || imgEl.dataset.rtaMounted) return;
    if (CR.shouldSkip(imgEl)) { imgEl.dataset.rtaMounted = 'skip'; return; }

    // Lazy-loaded images (Porto sets src only when the slide/thumb loads):
    // wait for the real source, then mount.
    var src = imgEl.currentSrc || imgEl.src;
    if (!src || src.indexOf('data:') === 0) {
      imgEl.addEventListener('load', function onLoad() {
        imgEl.removeEventListener('load', onLoad);
        mountImage(imgEl);
      });
      return;
    }
    imgEl.dataset.rtaMounted = '1';

    // Recolor by swapping the image source (Dawn/OS-2.0 friendly: no DOM
    // restructuring, plays nice with srcset/<picture>/sliders). The original
    // <img> stays in the DOM for SEO/LCP; we only change what it points to.
    imgEl.dataset.rtaBase = src;

    var px = targetSize(imgEl);
    var base = new Image();
    base.crossOrigin = 'anonymous';
    base.onload = function () {
      if (imgEl.dataset.rtaBase !== src) return; // remounted meanwhile — stale
      var canvas = document.createElement('canvas'); // offscreen
      var inst = new CR.Instance(base, canvas, {
        size: px,
        box: BOX,
        premiumBox: PREMIUM,
        standardBox: boxChoice === 'standard',
        onReady: function (ins) {                 // mask loaded + segmented
          var sel = current();
          if (sel) applyOne(ins, sel.hex);
          else if (PREMIUM) applyOne(ins, DEFAULT_HEX);
        }
      });
      inst._canvasEl = canvas;   // used by the product-page snapshot
      inst._imgEl = imgEl;
      instances.push(inst);
      var sel = current();
      if (sel) applyOne(inst, sel.hex);
      else if (PREMIUM) applyOne(inst, DEFAULT_HEX);
    };
    base.onerror = function () { /* leave original <img> visible */ };
    base.src = sizedSrc(src, px);
  }

  function applyOne(inst, hex) {
    inst.setColor(hex);                            // queues if mask not ready yet
    if (inst.disabled || !inst.ready) return;      // no mask => original photo stays
    var imgEl = inst._imgEl;
    if (!imgEl) return;
    // Wait for the grain texture; it re-applies everything when it arrives.
    if (texState === 'loading') return;
    // No color and no box to upgrade: leave the full-resolution photo alone.
    if (!hex && !inst.opts.standardBox && !(texState === 'ready' && inst.seg && inst.seg.box)) return;
    // Neutralize responsive sources so our recolored image wins.
    if (imgEl.parentNode && imgEl.parentNode.tagName === 'PICTURE') {
      var sources = imgEl.parentNode.querySelectorAll('source');
      for (var i = 0; i < sources.length; i++) sources[i].setAttribute('srcset', '');
    }
    imgEl.removeAttribute('srcset');
    imgEl.removeAttribute('data-srcset');
    imgEl.removeAttribute('sizes');
    // Keep the photo's true proportions regardless of the theme's width/height
    // attributes or fixed-ratio media container (prevents stretch/elongation).
    imgEl.removeAttribute('width');
    imgEl.removeAttribute('height');
    if (inst.seg) imgEl.style.aspectRatio = inst.seg.W + ' / ' + inst.seg.H;
    imgEl.style.height = 'auto';
    imgEl.style.objectFit = 'contain';
    imgEl.src = inst.snapshotDataURL();
  }

  function applyAll(hex) {
    for (var i = 0; i < instances.length; i++) applyOne(instances[i], hex);
  }

  function mountAll() {
    if (PRODUCT_SELECTOR) qsa(PRODUCT_SELECTOR).forEach(mountImage);
    // explicit opt-in images (e.g. the section's own sticky preview)
    qsa('img[data-recolor]').forEach(mountImage);
    if (GRID_SELECTOR) lazyGrid(GRID_SELECTOR);
  }

  function isCandidate(el) {
    if (!el || el.tagName !== 'IMG') return false;
    if (el.hasAttribute('data-recolor')) return true;
    try {
      if (PRODUCT_SELECTOR && el.matches(PRODUCT_SELECTOR)) return true;
      if (GRID_SELECTOR && el.matches(GRID_SELECTOR)) return true;
    } catch (e) {}
    return false;
  }

  function remountImage(imgEl) {
    // Drop the stale instance for this element, then mount fresh.
    for (var i = instances.length - 1; i >= 0; i--) {
      if (instances[i]._imgEl === imgEl) instances.splice(i, 1);
    }
    delete imgEl.dataset.rtaMounted;
    delete imgEl.dataset.rtaBase;
    mountImage(imgEl);
  }

  // Porto's gallery lazy-loads slides, clones them for the carousel, and swaps
  // the main image src on thumbnail clicks — all after boot. Watch the DOM and
  // (re)mount anything that appears or changes, so every gallery image stays
  // recolored. Our own writes are data: URLs and are ignored (no loops).
  function observeDynamicImages() {
    if (!('MutationObserver' in window)) return;
    var mo = new MutationObserver(function (muts) {
      for (var m = 0; m < muts.length; m++) {
        var mu = muts[m];
        if (mu.type === 'childList') {
          for (var a = 0; a < mu.addedNodes.length; a++) {
            var node = mu.addedNodes[a];
            if (node.nodeType !== 1) continue;
            if (isCandidate(node)) mountImage(node);
            else if (node.querySelectorAll) {
              var imgs = node.querySelectorAll('img');
              for (var k = 0; k < imgs.length; k++) if (isCandidate(imgs[k])) mountImage(imgs[k]);
            }
          }
        } else if (mu.type === 'attributes' && mu.attributeName === 'src') {
          var img = mu.target;
          if (!isCandidate(img)) continue;
          var s = img.src || '';
          if (s.indexOf('data:') === 0) continue;            // our own write
          if (!img.dataset.rtaMounted) { mountImage(img); continue; }
          if (img.dataset.rtaMounted === 'skip') continue;
          if (s === img.dataset.rtaBase) {
            // Theme reverted to the original photo — re-apply the recolor.
            var sel = current();
            if (sel) {
              for (var j = 0; j < instances.length; j++) {
                if (instances[j]._imgEl === img) { applyOne(instances[j], sel.hex); break; }
              }
            }
          } else {
            // Theme swapped to a different photo (thumbnail click) — rebuild.
            remountImage(img);
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
  }

  // ---- collection grid: viewport-only + cached -----------------------------
  function lazyGrid(selector) {
    if (!('IntersectionObserver' in window)) { qsa(selector).forEach(mountImage); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        mountImage(e.target);
        io.unobserve(e.target);
      });
    }, { rootMargin: '300px' });
    qsa(selector).forEach(function (img) { io.observe(img); });
  }

  // ---- selection state ------------------------------------------------------
  function current() { return CR.load(); }

  // ---- door style (chosen once with the color; rides to cart/checkout/order) --
  var DS_KEY = 'rta_door_style';
  var DS_PROP = 'Door style';
  // The colorized-cabinet URL rides to the order under an UNDERSCORED name. That
  // underscore is the whole point: Shopify hides underscored properties from the
  // cart, the order page and every notification template, so the link can only
  // ever be used the way it is meant to be used - as the src of an <img> that
  // shows the cabinet - and can never print itself as raw text next to a line.
  var PREVIEW_PROP = '_Preview';
  // These cabinets are made in the color the customer picks, so a line without
  // one cannot be built. Set by the section that renders the picker/bar, which
  // is also the only place a color CAN be picked — never turn this on where the
  // shopper has no way to choose, or add-to-cart becomes a dead end.
  var REQUIRE_COLOR = !!window.RTA_REQUIRE_COLOR;
  var DOOR_STYLES = (window.RTA_DOOR_STYLES || []).filter(Boolean);
  function loadStyle() {
    try {
      var v = localStorage.getItem(DS_KEY);
      return v && (!DOOR_STYLES.length || DOOR_STYLES.indexOf(v) !== -1) ? v : null;
    } catch (e) { return null; }
  }
  function saveStyle(v) { try { if (v) localStorage.setItem(DS_KEY, v); } catch (e) {} }
  function saveStyleToCart(v) {
    if (!v) return;
    var attrs = {}; attrs[DS_PROP] = v;
    fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () {});
  }
  function screenStyleSelect() { return document.getElementById('rta-door-style'); }
  function chosenStyle() {                       // color screen: dropdown value wins
    var sel = screenStyleSelect();
    return sel ? (sel.value || null) : loadStyle();
  }
  function populateStyleSelects() {
    var saved = loadStyle();
    qsa('select.rta-door-style-select').forEach(function (sel) {
      if (sel.dataset.rtaFilled) return;
      sel.dataset.rtaFilled = '1';
      DOOR_STYLES.forEach(function (name) {
        var o = document.createElement('option');
        o.value = name; o.textContent = name;
        sel.appendChild(o);
      });
      if (saved) sel.value = saved;
    });
  }
  function renderStyleLabels() {
    var v = loadStyle();
    qsa('[data-rta-ds-label]').forEach(function (el) { el.textContent = v ? 'Door style: ' + v : ''; });
    qsa('select.rta-door-style-select').forEach(function (sel) {
      if (sel.id !== 'rta-door-style' && v) sel.value = v;
      sel.classList.remove('rta-ds-missing');
    });
  }
  function showStyleNeeded(show) {
    var need = document.getElementById('rta-ds-need');
    if (need) need.hidden = !show;
    var sel = screenStyleSelect();
    if (show && sel) { sel.focus(); sel.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  }
  function swapDoorPhoto(style) {
    var img = document.querySelector('#rta-viz-root img[data-recolor]');
    if (!img) return;
    // Shaker is the native/default visualizer image. Restore it explicitly
    // whenever the shopper returns from another door style.
    var url = style === 'Shaker' ? img.dataset.defaultSrc :
      (window.RTA_DOOR_STYLE_IMAGES || {})[style];
    if (!url) return;
    if (img.dataset.rtaDoorStyle === style && img.src.indexOf(url) !== -1) return;
    img.dataset.rtaDoorStyle = style;
    img.src = url;
    img.removeAttribute('srcset');
    img.removeAttribute('data-srcset');
  }

  function wireStyleSelects() {
    var screen = screenStyleSelect();
    if (screen) screen.addEventListener('change', function () {
      if (screen.value) { showStyleNeeded(false); swapDoorPhoto(screen.value); }
    });
    // Product-page bar: a change there is saved straight away.
    var bar = document.getElementById('rta-door-style-bar');
    if (bar) bar.addEventListener('change', function () {
      if (!bar.value) return;
      saveStyle(bar.value);
      swapDoorPhoto(bar.value);
      saveStyleToCart(bar.value);
      renderStyleLabels();
      injectCartProps(current());
    });
  }

  // ---- write the properties as the form is submitted ------------------------
  // THIS is what makes the color reach the cart, and it is not belt-and-braces.
  // Dawn replaces the whole product-info section when the shopper changes the
  // variant — which on these cabinets IS choosing the size, so it happens on
  // nearly every order — and every hidden input we appended goes with it. Any
  // scheme that writes the inputs earlier (on load, on a color pick, on a timer)
  // loses that race: the shopper picks a size, clicks Add to cart, and the line
  // arrives with no color on it at all.
  //
  // A capture listener on the document runs before the form's own bubbling
  // handler, which is where Dawn builds the FormData, so the inputs are always
  // in place by the time it reads them however the form got there.
  function wireCartPropInjection() {
    // Some Shopify product-form variants construct the request from the Add
    // button click before a native submit event reaches the document. Inject at
    // both boundaries so the committed paint color cannot be lost to that race.
    document.addEventListener('click', function (e) {
      var target = e.target && e.target.closest ? e.target.closest('button[type="submit"], input[type="submit"]') : null;
      var f = target && (target.form || (target.closest && target.closest('form[action*="/cart/add"]')));
      if (!f || !f.matches || !f.matches('form[action*="/cart/add"]')) return;
      if (REQUIRE_COLOR && !current()) { showColorNeeded(); return; }
      injectCartProps(null);
    }, true);
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (!f || !f.matches || !f.matches('form[action*="/cart/add"]')) return;
      // A color is part of what is being ordered, so nothing goes in the cart
      // without one. stopImmediatePropagation keeps the theme's own submit
      // handler from running and adding the line behind our back.
      if (REQUIRE_COLOR && !current()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showColorNeeded();
        return;
      }
      injectCartProps(null);                 // null => use the committed color
      // Product page with no door style yet: point at the bar's dropdown.
      // Never blocks the add — trim has no door.
      var bar = document.getElementById('rta-door-style-bar');
      if (bar && !loadStyle()) bar.classList.add('rta-ds-missing');
    }, true);
  }

  var pendingSel = null;   // previewed but not yet committed via "Select this color"

  // Tap on a swatch/row: live preview only — recolor everything, arm the
  // Select button, but commit nothing until "Select this color" is pressed.
  function preview(sw) {
    pendingSel = sw;
    applyAll(sw.hex);
    var live = document.getElementById('rta-bar-live');
    if (live) live.textContent = colorLabel(sw);
    var btn = document.getElementById('rta-select-btn');
    if (btn) { btn.disabled = false; btn.textContent = 'Select ' + sw.name; }
    qsa('.rta-row[aria-selected="true"]').forEach(function (el) { el.setAttribute('aria-selected', 'false'); });
    var row = sw.code && document.querySelector('.rta-row[data-code="' + cssEsc(sw.code) + '"]');
    if (row) row.setAttribute('aria-selected', 'true');
    markStdPills(sw);
  }
  // Standard-finish pills show which one is active (previewed or committed).
  function markStdPills(sw) {
    qsa('.rta-std-pill').forEach(function (b) {
      b.setAttribute('aria-pressed', sw && !sw.code && b.dataset.name === sw.name ? 'true' : 'false');
    });
  }

  // "Select this color": commit — persist, write to cart, minimize the picker.
  // On the color screen a door style must be chosen alongside the color.
  function commit(sw) {
    if (!sw) return;
    var style = chosenStyle();
    if (screenStyleSelect() && !style) { preview(sw); showStyleNeeded(true); return; }
    if (style) saveStyle(style);
    showStyleNeeded(false);
    renderStyleLabels();
    applyAll(sw.hex);
    CR.save(sw);
    // One cart write at a time so neither attribute overwrites the other.
    Promise.resolve(CR.saveToCart(sw)).catch(function () {}).then(function () { saveStyleToCart(style); });
    renderBar(sw);
    injectCartProps(sw);
    syncPaymentButtons();
    collapseViz(sw);
  }

  function choose(sw) { preview(sw); commit(sw); } // programmatic/debug path
  window.RTA_chooseColor = choose; // exposed for inline handlers / debugging

  // ---- minimize / restore the visualizer ------------------------------------
  function collapseViz(sw) {
    var root = document.getElementById('rta-viz-root');
    var strip = document.getElementById('rta-viz-collapsed');
    if (!root || !strip) return;
    root.classList.add('rta-min');
    strip.querySelector('.rta-collapsed-sw').style.background = sw.hex;
    strip.querySelector('.rta-collapsed-name').textContent = sw.name;
    strip.querySelector('.rta-collapsed-code').textContent = sw.code;
    strip.hidden = false;
  }

  function expandViz() {
    var root = document.getElementById('rta-viz-root');
    var strip = document.getElementById('rta-viz-collapsed');
    if (!root) return;
    root.classList.remove('rta-min');
    if (strip) strip.hidden = true;
    var sel = pendingSel || current();
    var btn = document.getElementById('rta-select-btn');
    if (btn && sel) { btn.disabled = false; btn.textContent = 'Select ' + sel.name; }
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function wireCollapseControls() {
    var selectBtn = document.getElementById('rta-select-btn');
    if (selectBtn) selectBtn.addEventListener('click', function () { commit(pendingSel || current()); });
    var changeBtn = document.getElementById('rta-change-btn');
    if (changeBtn) changeBtn.addEventListener('click', expandViz);
    var barChange = document.getElementById('rta-bar-change');
    if (barChange) barChange.addEventListener('click', expandViz);
  }

  // ---- "Your selection" bar -------------------------------------------------
  function renderBar(sw) {
    var bar = document.getElementById('rta-selection-bar');
    if (!bar) return;
    if (!sw) {
      // No color yet. Where one is required the bar becomes the prompt to pick
      // it, rather than hiding and leaving the shopper to find out at the
      // Add to cart button that they cannot proceed.
      if (!REQUIRE_COLOR) { bar.hidden = true; return; }
      bar.hidden = false;
      bar.classList.add('rta-bar--needs-color');
      var change = bar.querySelector('.rta-bar-change');
      if (change) change.textContent = 'Pick a color';
      return;
    }
    bar.hidden = false;
    bar.classList.remove('rta-bar--needs-color', 'rta-bar--alert');
    var changeLink = bar.querySelector('.rta-bar-change');
    if (changeLink) changeLink.textContent = 'Change color';
    var sel = bar.querySelector('.rta-bar-sw');
    if (sel) sel.style.background = sw.hex;
    bar.querySelector('.rta-bar-name').textContent = sw.name;
    bar.querySelector('.rta-bar-code').textContent = sw.code;
  }

  // ---- phone layout for the product-page bar --------------------------------
  // At phone width the bar is one short row (color + Pick/Change). The door
  // style and Note fields are MOVED - the same nodes, so their ids and change
  // listeners keep working - to just above the product form (quantity/Add to cart).
  // Placeholders remember where they came from so a rotate/resize back to
  // desktop puts them back in the bar exactly as before.
  var PHONE = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
  var mobileFields = null;   // wrapper that holds the moved nodes above the form
  var movedHomes = [];       // [{ node, marker }]

  function productBar() {
    var bar = document.getElementById('rta-selection-bar');
    return bar && bar.classList.contains('rta-bar--product') ? bar : null;
  }
  // Where the fields go: just ahead of the main add-to-cart form (the one
  // holding the buttons, never Dawn's installment form, which also posts to
  // /cart/add). Kept OUTSIDE the <form> so pressing Go/Enter in the Note
  // field can't implicitly submit an add to cart.
  function fieldsAnchor() {
    var btns = qsa('form[action*="/cart/add"] .product-form__buttons');
    if (!btns.length) return null;
    var form = btns[0].closest('form');
    return (form && form.closest('product-form')) || form;
  }
  function placeFields() {
    var bar = productBar();
    if (!bar) return;
    if (PHONE && PHONE.matches) {
      if (!movedHomes.length) {
        ['.rta-ds-inline', '.rta-note-inline'].forEach(function (sel) {
          var node = bar.querySelector(sel);
          if (!node) return;
          var marker = document.createComment('rta-field-home');
          node.parentNode.insertBefore(marker, node);
          movedHomes.push({ node: node, marker: marker });
        });
      }
      if (!movedHomes.length) return;
      var anchor = fieldsAnchor();
      if (!anchor) return;
      // Re-homed on every check: if the theme ever re-renders the form, the
      // same nodes (listeners intact) go straight back in.
      if (!mobileFields) { mobileFields = document.createElement('div'); mobileFields.className = 'rta-mobile-fields'; }
      if (mobileFields.parentNode !== anchor.parentNode || mobileFields.nextSibling !== anchor) {
        anchor.parentNode.insertBefore(mobileFields, anchor);
      }
      movedHomes.forEach(function (h) {
        if (h.node.parentNode !== mobileFields) mobileFields.appendChild(h.node);
      });
    } else if (movedHomes.length) {
      movedHomes.forEach(function (h) {
        if (h.marker.parentNode) h.marker.parentNode.insertBefore(h.node, h.marker);
      });
      if (mobileFields && mobileFields.parentNode) mobileFields.parentNode.removeChild(mobileFields);
    }
  }
  // Sit the bar on top of the theme's mobile sticky toolbar, and pad the page
  // by both so the last of the content can still scroll into view. Phone only;
  // desktop keeps its original inline styles untouched.
  var bodyPadOrig = null;
  function layoutBar() {
    var bar = productBar();
    if (!bar) return;
    if (PHONE && PHONE.matches) {
      var tb = document.querySelector('.sticky-toolbar');
      var tbH = tb ? tb.offsetHeight : 0;          // 0 when the toolbar is hidden
      bar.style.bottom = tbH + 'px';
      if (bodyPadOrig === null) bodyPadOrig = document.body.style.paddingBottom;
      document.body.style.paddingBottom = ((bar.hidden ? 0 : bar.offsetHeight) + tbH) + 'px';
    } else if (bodyPadOrig !== null) {
      bar.style.bottom = '';
      document.body.style.paddingBottom = bodyPadOrig;
      bodyPadOrig = null;
    }
  }
  function phoneLayout() { placeFields(); layoutBar(); }

  // Shopify's dynamic checkout button ("Buy it now" / Shop Pay) goes straight to
  // checkout without ever submitting the form, so the submit gate below cannot
  // see it. While a color is still missing it is taken off the page; it comes
  // back the moment one is chosen. Re-applied on a timer because Dawn re-renders
  // the buy buttons whenever the variant changes.
  function syncPaymentButtons() {
    if (!REQUIRE_COLOR) return;
    var blocked = !current();
    qsa('.shopify-payment-button').forEach(function (el) {
      el.style.display = blocked ? 'none' : '';
    });
  }

  // Send the shopper to wherever a color can actually be chosen on this page:
  // the picker itself on the color collection, the bar's "Pick a color" link on
  // a product page.
  function showColorNeeded() {
    renderBar(null);
    var bar = document.getElementById('rta-selection-bar');
    if (bar) bar.classList.add('rta-bar--alert');
    var target = document.getElementById('rta-color-picker') ||
                 document.getElementById('rta-viz-root') || bar;
    if (target && target.scrollIntoView) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    var search = document.getElementById('rta-search-name');
    if (search) search.focus();
  }

  // ---- cart line-item property injection (product pages) --------------------
  // Adds hidden inputs to the product form so the order carries the color/note.
  function injectCartProps(sw) {
    var forms = qsa('form[action*="/cart/add"]');
    var style = loadStyle();
    // A committed color must reach the line whatever the caller passed. Without
    // this a call made for the door style alone could leave a cart line stating
    // its door style and no color at all.
    sw = sw || current();
    forms.forEach(function (form) {
      if (style) setProp(form, DS_PROP, style);
      if (window.RTA_BOX_CHOICE) setProp(form, BOX_PROP, boxLabel());
      if (!sw) return;
      var label = colorLabel(sw);
      setProp(form, 'Front color', label);
      var note = document.getElementById('rta-note');
      if (note) {
        var noteVal = (note.value || '').slice(0, 100).trim();
        if (noteVal) setProp(form, 'Note', noteVal);
      }
      // _Preview: a URL that RENDERS the colorized cabinet on demand — nothing is
      // uploaded or stored. This is what lets the emailed confirmation and the
      // printed invoice show the cabinet, since neither can run JavaScript.
      // Built only via RTACabinetPreview so the URL stays byte-identical across
      // callers; a value that differs would stop identical cabinets merging
      // into one cart line.
      if (window.RTACabinetPreview && window.RTA_PRODUCT_PHOTO) {
        var preview = window.RTACabinetPreview.url(window.RTA_PRODUCT_PHOTO, label,
          window.RTA_BOX_CHOICE ? boxLabel() : null);
        setProp(form, PREVIEW_PROP, preview || '');
      }
    });
  }
  // Called by the box cards. Repaints every photo in the chosen box and
  // updates the add-to-cart form.
  window.RTABox = {
    get: function () { return boxChoice; },
    set: function (v) {
      boxChoice = v === 'standard' ? 'standard' : 'premium';
      try { localStorage.setItem(BOX_KEY, boxChoice); } catch (e) {}
      var sel = current();
      instances.forEach(function (inst) {
        inst.opts.standardBox = boxChoice === 'standard';
        applyOne(inst, sel ? sel.hex : DEFAULT_HEX);
      });
      injectCartProps();
      document.dispatchEvent(new CustomEvent('rta:box', { detail: boxChoice }));
    }
  };

  function setProp(form, key, value) {
    var name = 'properties[' + key + ']';
    var input = form.querySelector('input[name="' + cssEsc(name) + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  // ---- UI build -------------------------------------------------------------
  function buildUI() {
    var root = document.getElementById('rta-color-picker');
    if (!root) return;

    // search fields
    var byName = root.querySelector('#rta-search-name');
    var byNum = root.querySelector('#rta-search-num');
    var listEl = root.querySelector('#rta-color-list');
    var expandBtn = root.querySelector('#rta-expand-colors');
    var paletteExpanded = false;
    var initialColors = (window.RTA_RECENT && window.RTA_RECENT.length ? window.RTA_RECENT : COLORS).slice(0, 6);

    // ---- standard-finish pills (same chip styling as the collection filter) ----
    // A pill is a color pick like a row tap: it previews through the same
    // recolor pipeline and arms "Select this color", which commits it.
    if (STANDARD.length && listEl) {
      injectCollStyles();
      var stdBar = document.createElement('div');
      stdBar.className = 'rta-coll-bar rta-std-bar';
      stdBar.setAttribute('role', 'group');
      stdBar.setAttribute('aria-label', 'Standard finishes');
      var committed = current();
      STANDARD.forEach(function (sw) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'rta-coll-chip rta-std-pill';
        b.dataset.name = sw.name;
        b.textContent = sw.name;
        b.setAttribute('aria-label', 'Standard finish: ' + sw.name);
        b.setAttribute('aria-pressed', committed && !committed.code && committed.name === sw.name ? 'true' : 'false');
        b.addEventListener('click', function () { preview(sw); });
        stdBar.appendChild(b);
      });
      listEl.parentNode.insertBefore(stdBar, listEl);
    }

    // ---- browse-by-collection filter chips ----
    var collections = window.SW_COLLECTIONS || {};
    var collNames = Object.keys(collections);
    var activeCollection = null;
    if (collNames.length && listEl) {
      injectCollStyles();
      var bar = document.createElement('div');
      bar.className = 'rta-coll-bar';
      bar.appendChild(collChip('All Colors', null, true));
      collNames.forEach(function (nm) { bar.appendChild(collChip(nm, nm, false)); });
      listEl.parentNode.insertBefore(bar, listEl);
    }
    function collChip(label, key, pressed) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'rta-coll-chip'; b.textContent = label;
      b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      b.addEventListener('click', function () {
        activeCollection = key;
        var chips = document.querySelectorAll('.rta-coll-chip:not(.rta-std-pill)');
        for (var i = 0; i < chips.length; i++) chips[i].setAttribute('aria-pressed', 'false');
        b.setAttribute('aria-pressed', 'true');
        render();
      });
      return b;
    }

    function render() {
      var nameq = (byName && byName.value || '').trim().toLowerCase();
      var numq = (byNum && byNum.value || '').replace(/sw|\s|#/gi, '').toLowerCase();
      var searching = nameq || numq;
      // Merge collection records into the search universe so newer Designer
      // colors remain discoverable even if a browser has an older cached deck.
      var all = {}, allColors = [];
      COLORS.forEach(function (c) { if (c && c.code) all[c.code] = c; });
      Object.keys(collections).forEach(function (key) {
        (collections[key] || []).forEach(function (c) { if (c && c.code && !all[c.code]) all[c.code] = c; });
      });
      Object.keys(all).forEach(function (code) { allColors.push(all[code]); });
      var base = allColors;
      if (activeCollection && collections[activeCollection]) {
        var inColl = {};
        collections[activeCollection].forEach(function (c) { inColl[c.code] = 1; });
        base = allColors.filter(function (c) { return inColl[c.code]; });
      }
      var filtered = base.filter(function (sw) {
        var okName = !nameq || sw.name.toLowerCase().indexOf(nameq) !== -1;
        var okNum = !numq || sw.code.replace(/sw|\s|#/gi, '').toLowerCase().indexOf(numq) !== -1;
        return okName && okNum;
      });

      // Keep the initial view focused: six starting colors only. Searching,
      // choosing a color collection, or selecting Browse all colors reveals the
      // full list without changing any selection or recolor behavior.
      var compactView = !searching && !activeCollection && !paletteExpanded;
      var groups = {};
      if (!compactView) {
        filtered.forEach(function (sw) {
          var b = bucketOf(sw.hex);
          (groups[b] = groups[b] || []).push(sw);
        });
      }

      listEl.innerHTML = '';
      if (compactView) {
        initialColors.forEach(function (sw) { listEl.appendChild(colorRow(sw)); });
      } else {
        BUCKETS.forEach(function (b) {
          if (!groups[b] || !groups[b].length) return;
          listEl.appendChild(groupBlock(b, groups[b]));
        });
      }
      if (!listEl.children.length) {
        var empty = document.createElement('p');
        empty.className = 'rta-empty';
        empty.textContent = 'No colors match that search.';
        listEl.appendChild(empty);
      }

      if (expandBtn) {
        var showExpandControl = !searching && !activeCollection;
        expandBtn.hidden = !showExpandControl;
        if (showExpandControl) {
          expandBtn.setAttribute('aria-expanded', paletteExpanded ? 'true' : 'false');
          expandBtn.textContent = paletteExpanded ? 'Show six colors' : 'Browse all Sherwin-Williams colors';
        }
      }
    }

    if (expandBtn) expandBtn.addEventListener('click', function () {
      paletteExpanded = !paletteExpanded;
      render();
    });
    if (byName) byName.addEventListener('input', render);
    if (byNum) byNum.addEventListener('input', render);
    render();
  }

  function groupBlock(title, list) {
    var wrap = document.createElement('div');
    wrap.className = 'rta-group';
    var h = document.createElement('h4');
    h.className = 'rta-group-title';
    h.textContent = title + ' (' + list.length + ')';
    wrap.appendChild(h);
    list.forEach(function (sw) { wrap.appendChild(colorRow(sw)); });
    return wrap;
  }

  function colorRow(sw) {
    var sel = current();
    var row = document.createElement('button');
    row.type = 'button';
    row.className = 'rta-row';
    row.dataset.code = sw.code;
    row.setAttribute('aria-selected', sel && sel.code === sw.code ? 'true' : 'false');
    row.innerHTML =
      '<span class="rta-sw" style="background:' + sw.hex + '"></span>' +
      '<span class="rta-row-name">' + esc(sw.name) + '</span>' +
      '<span class="rta-row-code">' + esc(sw.code) + '</span>' +
      '<span class="rta-check" aria-hidden="true">✓</span>';
    row.addEventListener('click', function () { preview(sw); });
    return row;
  }

  function swatchChip(sw) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'rta-chip';
    b.title = sw.name + ' ' + sw.code;
    b.style.background = sw.hex;
    b.addEventListener('click', function () { preview(sw); });
    return b;
  }

  // ---- helpers --------------------------------------------------------------
  function qsa(sel) { try { return Array.prototype.slice.call(document.querySelectorAll(sel)); } catch (e) { return []; } }
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]; }); }
  function cssEsc(s) { return String(s).replace(/"/g, '\\"'); }
  function injectCollStyles() {
    if (document.getElementById('rta-coll-style')) return;
    var s = document.createElement('style');
    s.id = 'rta-coll-style';
    s.textContent = '.rta-coll-bar{display:flex;flex-wrap:wrap;gap:6px;margin:0 0 12px}' +
      '.rta-coll-chip{padding:6px 12px;border:1px solid #d9d4cb;border-radius:999px;background:#fff;cursor:pointer;font-size:13px}' +
      '.rta-coll-chip[aria-pressed="true"]{background:#1c1a17;color:#f3efe7;border-color:#1c1a17}';
    document.head.appendChild(s);
  }

  // ---- boot -----------------------------------------------------------------
  function boot() {
    if (PREMIUM) {
      // Once the grain texture is in, re-render whatever is already showing.
      CR.loadBoxTexture(CFG.boxTexture).then(function (tex) {
        texState = tex ? 'ready' : 'failed';
        for (var i = 0; i < instances.length; i++) {
          var ins = instances[i];
          if (ins.ready && !ins.disabled) applyOne(ins, ins.hex || null);
        }
      });
    }
    mountAll();
    observeDynamicImages();
    buildUI();
    populateStyleSelects();
    wireStyleSelects();
    if (loadStyle()) swapDoorPhoto(loadStyle());
    wireCartPropInjection();
    wireCollapseControls();
    renderStyleLabels();
    var sel = current();
    if (sel && screenStyleSelect() && !loadStyle()) {
      // Color chosen before door styles existed here: keep the picker open with
      // the color armed so the shopper adds a door style, then selects.
      pendingSel = sel;
      renderBar(sel);
      injectCartProps(sel);
      preview(sel);
    }
    else if (sel) {
      // Returning shopper with a committed color: recolor everything (handled
      // per-image via onReady), show the bar, and start minimized.
      pendingSel = sel;
      renderBar(sel);
      injectCartProps(sel);
      collapseViz(sel);
      var live = document.getElementById('rta-bar-live');
      if (live) live.textContent = colorLabel(sel);
    }
    else {
      renderBar(null);                          // prompt for a color where one is required
      if (loadStyle()) injectCartProps(null);   // door style alone still rides along
      if (DEFAULT_HEX) applyAll(DEFAULT_HEX);
    }
    // else: leave photos as-is (Snow White) until a deliberate pick — decision A.

    phoneLayout();
    if (PHONE) {
      if (PHONE.addEventListener) PHONE.addEventListener('change', phoneLayout);
      else if (PHONE.addListener) PHONE.addListener(phoneLayout);
    }
    window.addEventListener('resize', layoutBar);

    // Correctness comes from wireCartPropInjection() above; this only keeps the
    // inputs visible in the form between renders, so anything that inspects the
    // form directly (an app, a debugging session) sees the real values rather
    // than an empty form. Re-writing is idempotent.
    setInterval(function () { injectCartProps(null); syncPaymentButtons(); phoneLayout(); }, 1000);
    syncPaymentButtons();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
